import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Package, RefreshCw, Phone, MapPin, ChevronRight, 
  Loader2, Clock, DollarSign, RotateCcw, CheckCircle,
  AlertTriangle, Navigation, QrCode
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/context/SettingsContext';
import DriverActionPanel from '@/components/DriverActionPanel';
import DriverQrScanner from '@/components/DriverQrScanner';
import DriverReturnPickups from '@/components/DriverReturnPickups';
import DriverIdleWarningBanner from '@/components/DriverIdleWarningBanner';
import DriverIncentiveWidget from '@/components/DriverIncentiveWidget';
import ImageLightbox from '@/components/ImageLightbox';
import PullToRefresh from '@/components/PullToRefresh';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Hook to track driver location
const useDriverLocationTracking = (isActive) => {
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  
  const sendLocation = useCallback(async (position) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/tracking/driver/location`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            heading: position.coords.heading,
            speed: position.coords.speed,
            accuracy: position.coords.accuracy
          }
        }
      );
    } catch (error) {
      console.error('Failed to send location:', error);
    }
  }, []);
  
  useEffect(() => {
    if (!isActive || !navigator.geolocation) return;
    
    // Start watching position
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => sendLocation(position),
      (error) => console.error('Geolocation error:', error),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000
      }
    );
    
    // Also send location periodically (every 15 seconds)
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => sendLocation(position),
        (error) => console.error('Geolocation error:', error),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }, 15000);
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, sendLocation]);
};

const DriverOrdersPage = () => {
  const { formatCurrency } = useSettings();
  const [orders, setOrders] = useState([]);
  const [canMarkFailed, setCanMarkFailed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showNavApps, setShowNavApps] = useState(false);
  const [preferredNavApp, setPreferredNavApp] = useState(() => localStorage.getItem('driver_nav_app') || '');

  const NAV_APPS = {
    google: { label: 'Google Maps', url: (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}` },
    waze: { label: 'Waze', url: (lat, lng) => `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` },
    apple: { label: 'Apple Maps', url: (lat, lng) => `https://maps.apple.com/?daddr=${lat},${lng}` },
  };

  const openNavApp = (appKey, location) => {
    localStorage.setItem('driver_nav_app', appKey);
    setPreferredNavApp(appKey);
    setShowNavApps(false);
    window.open(NAV_APPS[appKey].url(location.lat, location.lng), '_blank');
  };
  const [filter, setFilter] = useState('active'); // active, completed, returns
  const [returnPickupCount, setReturnPickupCount] = useState(0);
  
  // Track location when there are active "on_the_way" orders
  const hasOnTheWayOrders = orders.some(o => o.fulfillment_status === 'on_the_way');
  useDriverLocationTracking(hasOnTheWayOrders);

  const fetchOrders = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/orders/my-deliveries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
      setCanMarkFailed(response.data.driver_can_mark_failed !== false);
    } catch (error) {
      // Fallback to regular orders endpoint filtered by driver
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data.orders || []);
      } catch (err) {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    // Preload return pickup count so the Returns tab shows a badge
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE_URL}/v1/returns/driver/my-pickups`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setReturnPickupCount(res.data.active_count || 0)).catch(() => {});
  }, [fetchOrders]);

  const handleOrderUpdate = (updatedOrder) => {
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
    setSelectedOrder(updatedOrder);
  };

  // QR deep link: ?openOrder=<id> opens that order directly (packing slip scan)
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkHandled = useRef(false);
  const [scanAccept, setScanAccept] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const oid = searchParams.get('openOrder');
    if (!oid || loading || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    setSearchParams({});
    const o = orders.find(x => x.id === oid || x.order_number === oid);
    if (o) {
      setSelectedOrder(o);
      if (['pending', 'packed', 'ready_for_dispatch', 'assigned', 'reopened'].includes(o.fulfillment_status)) {
        setScanAccept(o);
      }
    } else {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.is_admin || user?.roles?.includes('admin')) {
        navigate(`/orders?openOrder=${oid}`);
      } else {
        toast.error('This order is not in your deliveries');
      }
    }
  }, [orders, loading, searchParams, setSearchParams, navigate]);

  const handleScanAccept = async () => {
    if (!scanAccept) return;
    setAccepting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${scanAccept.id}/driver-action`,
        { action: 'accept' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Order accepted');
      handleOrderUpdate(response.data.order);
      setScanAccept(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept order');
    } finally {
      setAccepting(false);
    }
  };

  // In-app camera scanner (bulk dispatch handovers)
  const [showScanner, setShowScanner] = useState(false);
  const [scanSessionCount, setScanSessionCount] = useState(0);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  const handleScannedCode = async (text) => {
    // QR encodes .../my-deliveries?openOrder=<id>; barcode encodes the order number
    const m = String(text).match(/openOrder=([\w-]+)/);
    const key = m ? m[1] : String(text).trim();
    const o = ordersRef.current.find(x => x.id === key || x.order_number === key);
    if (!o) {
      toast.error('Order not found in your deliveries');
      return;
    }
    if (['pending', 'packed', 'ready_for_dispatch', 'assigned', 'reopened'].includes(o.fulfillment_status)) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_BASE_URL}/orders/${o.id}/driver-action`,
          { action: 'accept' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrders(prev => prev.map(x => x.id === o.id ? response.data.order : x));
        setScanSessionCount(c => c + 1);
        toast.success(`#${o.order_number} accepted`);
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to accept #${o.order_number}`);
      }
    } else {
      // Already accepted/further along — open the order instead
      setShowScanner(false);
      setSelectedOrder(o);
      toast.info(`#${o.order_number} is already ${(o.fulfillment_status || '').replace(/_/g, ' ')}`);
    }
  };

  const activeOrders = orders.filter(o => 
    !['delivered', 'fulfilled', 'cancelled', 'failed'].includes(o.fulfillment_status)
  );
  
  const completedOrders = orders.filter(o => {
    if (!['delivered', 'fulfilled', 'cancelled', 'failed'].includes(o.fulfillment_status)) return false;
    // Keep the completed list short: only the last 3 days
    const when = o.delivered_at || o.updated_at || o.created_at;
    return !when || (Date.now() - new Date(when).getTime()) <= 3 * 24 * 60 * 60 * 1000;
  });

  const displayOrders = filter === 'active' ? activeOrders : completedOrders;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      reopened: 'bg-blue-500',
      ready_for_dispatch: 'bg-blue-500',
      assigned: 'bg-blue-500',
      packed: 'bg-amber-500',
      accepted: 'bg-purple-500',
      picked: 'bg-indigo-500',
      out_for_delivery: 'bg-cyan-500',
      on_the_way: 'bg-cyan-500',
      delivered: 'bg-green-500',
      fulfilled: 'bg-green-500',
      failed: 'bg-red-500',
      cancelled: 'bg-gray-500',
      delivery_rescheduled: 'bg-amber-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'New',
      reopened: 'Reopened',
      ready_for_dispatch: 'Ready to Pick',
      assigned: 'Assigned',
      packed: 'Packed',
      accepted: 'Accepted',
      picked: 'Picked',
      out_for_delivery: 'On Way',
      on_the_way: 'On Way',
      delivered: 'Delivered',
      fulfilled: 'Complete',
      failed: 'Failed',
      cancelled: 'Cancelled',
      delivery_rescheduled: 'Rescheduled',
    };
    return texts[status] || status;
  };

  const formatAddress = (address) => {
    if (!address) return '';
    if (typeof address === 'object') {
      return [address.address1, address.address2, address.city, address.province || address.state, address.country]
        .filter(Boolean).join(', ');
    }
    return String(address);
  };

  const openMaps = (address) => {
    const encoded = encodeURIComponent(formatAddress(address));
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, '_blank');
  };

  // Pull to refresh handler - defined before any early returns
  const handlePullRefresh = useCallback(async () => {
    await fetchOrders(true);
  }, [fetchOrders]);

  // Full screen order detail view
  if (selectedOrder) {
    return (
      <div className="min-h-screen bg-wt-bg" data-testid="driver-order-detail">
        <DriverIdleWarningBanner />
        {/* Scan-to-accept confirmation (QR deep link) */}
        {scanAccept && scanAccept.id === selectedOrder.id && (
          <div className="fixed inset-x-0 bottom-0 z-50 p-4" data-testid="scan-accept-sheet">
            <div className="mx-auto max-w-md bg-wt-surface border border-wt-gold/50 rounded-2xl p-4 shadow-2xl">
              <div className="flex items-center gap-2 mb-1">
                <QrCode className="w-5 h-5 text-wt-gold" />
                <span className="font-bold text-wt-text-primary">Scanned #{scanAccept.order_number}</span>
              </div>
              <p className="text-sm text-wt-text-secondary mb-3">Accept this delivery now?</p>
              <div className="flex gap-2">
                <button
                  onClick={handleScanAccept}
                  disabled={accepting}
                  className="flex-1 py-3 rounded-xl bg-wt-gold text-black font-bold active:scale-98 disabled:opacity-60"
                  data-testid="scan-accept-btn"
                >
                  {accepting ? 'Accepting…' : 'ACCEPT ORDER'}
                </button>
                <button
                  onClick={() => setScanAccept(null)}
                  className="px-4 py-3 rounded-xl border border-wt-border text-wt-text-secondary"
                  data-testid="scan-accept-dismiss"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="sticky top-0 z-10 bg-wt-surface border-b border-wt-border p-4 flex items-center justify-between">
          <button 
            onClick={() => setSelectedOrder(null)}
            className="text-wt-text-secondary text-lg"
            data-testid="back-button"
          >
            ← Back
          </button>
          <span className="text-lg font-bold text-wt-gold">#{selectedOrder.order_number}</span>
          <button 
            onClick={() => fetchOrders(true)}
            className="p-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Customer Card */}
          <div className="bg-wt-surface rounded-2xl p-4 border border-wt-border">
            <p className="text-xl font-bold text-wt-text-primary mb-3">
              {selectedOrder.customer_name || 'Customer'}
            </p>
            
            {/* Delivery note from ops */}
            {selectedOrder.driver_note && (
              <div className="w-full mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl" data-testid="driver-note-display">
                <p className="text-xs font-bold text-amber-400 mb-0.5">📝 Delivery Note</p>
                <p className="text-sm text-wt-text-primary">{selectedOrder.driver_note}</p>
              </div>
            )}

            {/* Customer Location Pin - Priority Navigation */}
            {selectedOrder.delivery_location && selectedOrder.delivery_location.lat && selectedOrder.delivery_location.lng && (
              <div className="mb-3">
                <button
                  onClick={() => {
                    if (preferredNavApp && NAV_APPS[preferredNavApp]) {
                      openNavApp(preferredNavApp, selectedOrder.delivery_location);
                    } else {
                      setShowNavApps(!showNavApps);
                    }
                  }}
                  className="flex items-start gap-3 w-full text-left p-3 bg-green-500/10 rounded-xl active:scale-98 border border-green-500/30"
                  data-testid="navigate-to-customer-location"
                >
                  <Navigation className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-green-400">
                        {selectedOrder.delivery_location.source === 'manual_entry' ? 'Location Pin (added by team)' : 'Customer Shared Location'}
                      </span>
                      <Badge className="bg-green-500/20 text-green-400 text-xs px-1.5 py-0.5">
                        Pinned
                      </Badge>
                    </div>
                    <p className="text-base text-green-400 font-bold flex items-center gap-1">
                      {preferredNavApp && NAV_APPS[preferredNavApp]
                        ? `Navigate with ${NAV_APPS[preferredNavApp].label} →`
                        : `Navigate to Location ${showNavApps ? '▾' : '→'}`}
                    </p>
                  </div>
                </button>
                {preferredNavApp && NAV_APPS[preferredNavApp] && (
                  <button
                    onClick={() => setShowNavApps(!showNavApps)}
                    className="text-xs text-wt-text-muted underline mt-1.5 ml-1"
                    data-testid="change-nav-app"
                  >
                    Change maps app
                  </button>
                )}
                {showNavApps && (
                  <div className="flex gap-2 mt-2" data-testid="nav-app-chooser">
                    {Object.entries(NAV_APPS).map(([key, app]) => (
                      <button
                        key={key}
                        onClick={() => openNavApp(key, selectedOrder.delivery_location)}
                        className={`flex-1 flex flex-col items-center gap-1 p-3 bg-wt-bg rounded-xl border active:scale-95 ${preferredNavApp === key ? 'border-green-500/50' : 'border-wt-border'}`}
                        data-testid={`nav-${key}`}
                      >
                        {key === 'waze'
                          ? <Navigation className="w-5 h-5 text-sky-400" />
                          : <MapPin className={`w-5 h-5 ${key === 'google' ? 'text-green-400' : 'text-wt-text-muted'}`} />}
                        <span className="text-xs text-wt-text-primary font-medium">{app.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Address — when a location pin exists it is the single source of
                navigation; show the written address as plain reference text */}
            {selectedOrder.delivery_location && selectedOrder.delivery_location.lat && selectedOrder.delivery_location.lng ? (
              <div className="flex items-start gap-3 w-full text-left mb-3 p-3 bg-wt-bg rounded-xl" data-testid="address-reference">
                <MapPin className="w-6 h-6 text-wt-text-muted flex-shrink-0 mt-0.5" />
                <p className="text-base text-wt-text-secondary flex-1">
                  {formatAddress(selectedOrder.shipping_address)}
                </p>
              </div>
            ) : (
              <button 
                onClick={() => openMaps(selectedOrder.shipping_address)}
                className="flex items-start gap-3 w-full text-left mb-3 p-3 bg-wt-bg rounded-xl active:scale-98"
                data-testid="open-maps-button"
              >
                <MapPin className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-base text-wt-text-secondary">
                    {formatAddress(selectedOrder.shipping_address)}
                  </p>
                  <p className="text-sm text-blue-400 mt-1 flex items-center gap-1">
                    <Navigation className="w-4 h-4" /> Open in Maps
                  </p>
                </div>
              </button>
            )}

            {/* Phone */}
            {selectedOrder.customer_phone && (
              <a 
                href={`tel:${selectedOrder.customer_phone}`}
                className="flex items-center gap-3 p-3 bg-green-500/10 rounded-xl active:scale-98"
                data-testid="call-customer-button"
              >
                <Phone className="w-6 h-6 text-green-400" />
                <span className="text-lg text-green-400 font-medium">{selectedOrder.customer_phone}</span>
              </a>
            )}
          </div>

          {/* Order Items Summary */}
          <div className="bg-wt-surface rounded-2xl p-4 border border-wt-border">
            <p className="text-lg font-semibold text-wt-text-primary mb-3 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {selectedOrder.items?.length || 0} Item(s)
            </p>
            <div className="space-y-2">
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-wt-bg rounded-lg">
                  {item.image_url ? (
                    <ImageLightbox 
                      src={item.image_url} 
                      alt={item.name || item.title}
                      className="w-16 h-16"
                      thumbnailClassName="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-wt-surface rounded flex items-center justify-center">
                      <Package className="w-8 h-8 text-wt-text-muted" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-wt-text-primary truncate">{item.name || item.title}</p>
                    <p className="text-xs text-wt-text-muted">Qty: {item.quantity}</p>
                    {item.sku && <p className="text-xs text-wt-text-muted">SKU: {item.sku}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-wt-surface rounded-2xl p-4 border border-wt-border">
            <div className="flex items-center justify-between">
              <span className="text-base text-wt-text-secondary">Total Amount</span>
              <span className="text-2xl font-bold text-wt-gold">
                {formatCurrency(selectedOrder.total_amount)}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-wt-text-muted">Payment Status</span>
              <Badge className={selectedOrder.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                {selectedOrder.payment_status === 'paid' ? 'PAID' : 'COD'}
              </Badge>
            </div>
          </div>

          {/* Driver Actions */}
          <DriverActionPanel 
            order={selectedOrder}
            onOrderUpdate={handleOrderUpdate}
            canMarkFailed={canMarkFailed}
          />
        </div>
      </div>
    );
  }

  // Orders List View
  return (
    <PullToRefresh onRefresh={handlePullRefresh} disabled={loading}>
    <div className="min-h-screen bg-wt-bg" data-testid="driver-orders-page">
      <DriverIdleWarningBanner />
      {showScanner && (
        <DriverQrScanner
          onScan={handleScannedCode}
          onClose={() => setShowScanner(false)}
          acceptedCount={scanSessionCount}
        />
      )}
      {/* Header */}
      <div className="sticky top-0 z-10 bg-wt-surface border-b border-wt-border">
        <div className="p-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-wt-gold">My Deliveries</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setScanSessionCount(0); setShowScanner(true); }}
              className="p-3 bg-wt-gold rounded-xl active:scale-95"
              data-testid="open-scanner-button"
            >
              <QrCode className="w-6 h-6 text-black" />
            </button>
            <button 
              onClick={() => fetchOrders(true)}
              disabled={refreshing}
              className="p-3 bg-wt-bg rounded-xl active:scale-95"
              data-testid="refresh-orders-button"
            >
              <RefreshCw className={`w-6 h-6 ${refreshing ? 'animate-spin text-wt-accent' : 'text-wt-text-muted'}`} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex p-2 gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold text-base transition-all
              ${filter === 'active' 
                ? 'bg-wt-accent text-white' 
                : 'bg-wt-bg text-wt-text-muted'}`}
            data-testid="active-filter-tab"
          >
            Active ({activeOrders.length})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold text-base transition-all
              ${filter === 'completed' 
                ? 'bg-wt-accent text-white' 
                : 'bg-wt-bg text-wt-text-muted'}`}
            data-testid="completed-filter-tab"
          >
            Completed ({completedOrders.length})
          </button>
          <button
            onClick={() => setFilter('returns')}
            className={`flex-1 py-3 px-4 rounded-xl font-semibold text-base transition-all
              ${filter === 'returns' 
                ? 'bg-wt-accent text-white' 
                : 'bg-wt-bg text-wt-text-muted'}`}
            data-testid="returns-filter-tab"
          >
            Returns{returnPickupCount > 0 ? ` (${returnPickupCount})` : ''}
          </button>
        </div>
      </div>

      {/* Incentive Progress */}
      <DriverIncentiveWidget refreshSignal={`${activeOrders.length}-${completedOrders.length}`} />

      {/* Orders List */}
      <div className="p-4 space-y-3">
        {filter === 'returns' ? (
          <DriverReturnPickups onCountChange={setReturnPickupCount} />
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-wt-accent" />
          </div>
        ) : displayOrders.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-wt-text-muted mx-auto mb-4" />
            <p className="text-xl text-wt-text-muted">
              {filter === 'active' ? 'No active deliveries' : 'No completed deliveries'}
            </p>
          </div>
        ) : (
          displayOrders.map(order => (
            <button
              key={order.id}
              onClick={() => setSelectedOrder(order)}
              className="w-full bg-wt-surface rounded-2xl p-4 border border-wt-border text-left active:scale-98 transition-transform"
              data-testid={`order-card-${order.order_number}`}
            >
              {/* Header Row */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xl font-bold text-wt-gold">#{order.order_number}</span>
                <div className={`${getStatusColor(order.fulfillment_status)} text-white px-3 py-1 rounded-full text-sm font-semibold`}>
                  {getStatusText(order.fulfillment_status)}
                </div>
              </div>

              {/* Customer & Address */}
              <p className="text-base font-medium text-wt-text-primary mb-1">
                {order.customer_name || 'Customer'}
              </p>
              <p className="text-sm text-wt-text-muted line-clamp-1 mb-3">
                {typeof order.shipping_address === 'object' 
                  ? [order.shipping_address?.address1, order.shipping_address?.city, order.shipping_address?.country].filter(Boolean).join(', ')
                  : order.shipping_address}
              </p>

              {/* Footer Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Amount */}
                  <span className="text-base font-semibold text-wt-text-primary">
                    {formatCurrency(order.total_amount)}
                  </span>
                  
                  {/* Special Badges */}
                  {order.return_replacement_type && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Return
                    </Badge>
                  )}
                  {order.is_reopened && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      Reopened
                    </Badge>
                  )}
                </div>
                <ChevronRight className="w-6 h-6 text-wt-text-muted" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
    </PullToRefresh>
  );
};

export default DriverOrdersPage;
